import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import AppStoreBadge from "./AppStoreBadge";
import { art } from "./config";

// Little exchanges that float around the phone. Leo gets it wrong; the child sorts it out.
const EXCHANGES = [
  {
    tag: "Year 3 · Times tables",
    leo: "Seven eights… is that fifty-four? I always muddle that one.",
    kid: "No! It's 56. Five, six, seven, eight — 56 = 7 × 8!",
  },
  {
    tag: "Year 4 · Division",
    leo: "24 cupcakes, 8 on each tray. So we need… loads of trays?",
    kid: "Just three! 24 divided by 8 is 3.",
  },
  {
    tag: "Year 2 · Fractions",
    leo: "A quarter's bigger than a half, right? Four's bigger than two.",
    kid: "Nope — cut a pizza in four, the slices get smaller!",
  },
  {
    tag: "Year 5 · Money",
    leo: "I've got £2 and the comic is £1.35. Do I get change?",
    kid: "Yes, 65p! Count up from £1.35.",
  },
];

const STEP_MS = 2600;

export function Phone({ children, className = "" }) {
  return (
    <div className={`phone ${className}`}>
      <div className="phone-screen">
        <div className="island" aria-hidden />
        {children}
      </div>
    </div>
  );
}

export function Bars({ count = 5, idle = false, className = "" }) {
  return (
    <span className={`bars ${idle ? "is-idle" : ""} ${className}`} aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <i key={i} style={count > 5 ? { animationDelay: `${-((i * 37) % 11) / 10}s` } : undefined} />
      ))}
    </span>
  );
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setReduced(mq.matches);
    const on = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

export default function Hero() {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [beat, setBeat] = useState(0); // 0 hidden, 1 leo, 2 leo + kid
  const [voice, setVoice] = useState(false); // playing Leo's intro with sound
  const tiltRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);

  // Browsers only allow sound after a tap: restart from 0:00 so the voice
  // lines up with the burned-in captions, play once, then go back to the
  // silent loop.
  const toggleVoice = () => {
    const v = videoRef.current;
    if (!v) return;
    if (voice) {
      v.muted = true;
      v.loop = true;
      setVoice(false);
      return;
    }
    window.posthog?.capture?.("hero_voice_play");
    v.muted = false;
    v.loop = false;
    v.currentTime = 0;
    setVoice(true);
    v.play().catch(() => {
      v.muted = true;
      v.loop = true;
      setVoice(false);
    });
  };

  const onVideoEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.loop = true;
    setVoice(false);
    v.currentTime = 0;
    if (!reduced) v.play().catch(() => {});
  };

  // Cycle the exchanges: Leo asks, the child answers, both fade, next.
  useEffect(() => {
    let t;
    const run = (b) => {
      setBeat(b);
      if (b === 0) {
        t = setTimeout(() => run(1), 500);
      } else if (b === 1) {
        t = setTimeout(() => run(2), STEP_MS);
      } else {
        t = setTimeout(() => {
          setBeat(0);
          t = setTimeout(() => {
            setI((n) => (n + 1) % EXCHANGES.length);
            run(1);
          }, 550);
        }, STEP_MS + 900);
      }
    };
    run(0);
    return () => clearTimeout(t);
  }, []);

  // Gentle 3D tilt that follows the pointer (desktop, motion allowed).
  useEffect(() => {
    if (reduced) return;
    const stage = stageRef.current;
    const el = tiltRef.current;
    if (!stage || !el || !window.matchMedia("(pointer: fine)").matches) return;
    let raf = 0;
    const onMove = (e) => {
      const r = stage.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / window.innerWidth;
      const y = (e.clientY - (r.top + r.height / 2)) / window.innerHeight;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform = `rotateY(${x * 14}deg) rotateX(${-y * 10}deg)`;
      });
    };
    const onLeave = () => (el.style.transform = "");
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, [reduced]);

  const ex = EXCHANGES[i];

  return (
    <section className="hero" data-testid="hero-section">
      <div className="wrap">
        <div className="hero-grid">
          <div>
            <a href="#listen" className="pill rv" style={{ "--d": "0ms" }}>
              <b>New</b> Leo is on the App Store
            </a>
            <h1 className="display rv" style={{ "--d": "80ms" }} data-testid="hero-headline">
              Hi, I'm Leo.
              <br />
              I'm <span className="said">rubbish</span> at maths.
            </h1>
            <p className="lede rv" style={{ "--d": "160ms" }} data-testid="hero-subtext">
              I'm a talking maths friend for children aged 5–11. I get stuck, I muddle things up,
              and your child explains them to me. <strong>Teaching me is how they learn it</strong>{" "}
              — they just think they're helping a mate.
            </p>
            <div className="hero-ctas rv" style={{ "--d": "240ms" }}>
              <AppStoreBadge testId="hero-app-store" />
              <a
                href="#listen"
                className="btn btn--ghost"
                data-testid="hero-listen"
                onClick={() => window.dispatchEvent(new Event("leo:listen"))}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M7 4.5v15l12.5-7.5z" />
                </svg>
                Hear us talk
              </a>
            </div>
            <div className="hero-meta rv" style={{ "--d": "320ms" }}>
              <span>
                <Check size={16} strokeWidth={2.5} /> Free to try
              </span>
              <span>
                <Check size={16} strokeWidth={2.5} /> Years 1–6, National Curriculum
              </span>
              <span>
                <Check size={16} strokeWidth={2.5} /> Voice first, no typing
              </span>
            </div>
          </div>

          <div className="stage rv" style={{ "--d": "200ms" }} ref={stageRef}>
            <div className="stage-glow" aria-hidden />
            <span className="ring" aria-hidden />
            <span className="ring" aria-hidden />
            <span className="ring" aria-hidden />

            <div className="phone-tilt" ref={tiltRef}>
              <Phone>
                <div className="statusbar" aria-hidden>
                  <span>9:41</span>
                  <i />
                </div>
                <video
                  ref={videoRef}
                  onEnded={onVideoEnded}
                  src={`${process.env.PUBLIC_URL}/leo-hero.mp4`}
                  poster={art("leo-hero-poster.jpg")}
                  autoPlay={!reduced}
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-label="Leo waving hello"
                  data-testid="hero-video"
                />
                <button
                  type="button"
                  className={`hear ${voice ? "is-on" : ""}`}
                  onClick={toggleVoice}
                  aria-pressed={voice}
                  data-testid="hero-hear-leo"
                >
                  {voice ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M4 9v6h4l5 4V5L8 9H4Zm12.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4ZM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6Z" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M4 9v6h4l5 4V5L8 9H4Zm12.6 3 2.7-2.7-1.1-1.1-2.7 2.7-2.7-2.7-1.1 1.1 2.7 2.7-2.7 2.7 1.1 1.1 2.7-2.7 2.7 2.7 1.1-1.1-2.7-2.7Z" />
                    </svg>
                  )}
                  {voice ? "Mute" : "Tap to hear Leo"}
                </button>
                <div className="talkbar" aria-hidden>
                  <span className="talkbar-mic">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 15a3.5 3.5 0 0 0 3.5-3.5v-6a3.5 3.5 0 1 0-7 0v6A3.5 3.5 0 0 0 12 15Zm6-3.5a.9.9 0 1 0-1.8 0 4.2 4.2 0 0 1-8.4 0 .9.9 0 1 0-1.8 0 6 6 0 0 0 5.1 5.93V20H8.9a.9.9 0 1 0 0 1.8h6.2a.9.9 0 1 0 0-1.8h-2.2v-2.57A6 6 0 0 0 18 11.5Z" />
                    </svg>
                  </span>
                  <span className="talkbar-label">
                    {voice ? "Leo's talking" : beat === 2 ? "You're talking" : "Leo's talking"}
                    <span>{voice ? "Say hi back!" : beat === 2 ? "Leo is listening…" : "Just talk back"}</span>
                  </span>
                  <Bars idle={!voice && beat === 0} />
                </div>
              </Phone>
            </div>

            <div className={`bubbles ${voice ? "is-quiet" : ""}`} aria-hidden>
              <span className="sticker-tag" key={`tag-${i}`}>
                {ex.tag}
              </span>
              <div className={`bubble bubble--leo ${beat >= 1 ? "is-on" : ""}`}>
                <span className="bubble-who">
                  <img src={art("leo-mark-160.png")} alt="" /> Leo
                </span>
                {ex.leo}
              </div>
              <div className={`bubble bubble--kid ${beat >= 2 ? "is-on" : ""}`}>
                <span className="bubble-who">Your child</span>
                {ex.kid}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
